<template>
  <div
    class="RelationshipComponent"
    :key="forWhomPeople"
  >
    <template
      v-for="(field, index) in insureds"
      :key="index"
    >
      <div v-if="!field.isHolder">
        <BaseSelect
          :id="'relationship_' + index"
          :label="t('components.RelationshipComponent.label', { n: index + 1 })"
          :items="options[index]"
          item-title="label"
          item-value="value"
          density="compact"
          variant="outlined"
          v-model="field.relationship"
          :rules="[validationRules.required]"
          @update:model-value="onChange(field.relationship, index)"
        />
      </div>
    </template>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, watch, onBeforeMount } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PropType, Ref } from 'vue'

import { arrayRange } from '@/utils/array.utils'
import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
import type { FormAnswersType, OptionsValuesInterface } from '@/modules/core/models/types'
import BaseSelect from '@/modules/core/components/ui/BaseSelect.vue'
import DentalConfigurations from '@/modules/dental/common/models/APP/DentalConfigurations'
import Insured from '@/modules/common/common/models/APP/Insured'
import UserDetails from '@/modules/common/hiring/models/APP/UserDetails'
import AddressDetails from '@/modules/common/hiring/models/APP/AddressDetails'
import { useRules } from '@/modules/core/composables/useRulesComposable'

const { t } = useI18n()
const { required } = useRules()

const props = defineProps({
  answers: {
    type: Object as PropType<FormAnswersType>,
    default: () => {}
  },
  configurations: DentalConfigurations,
  externalInit: {
    type: Boolean,
    default: () => false
  }
})

const forWhomPeople = computed(() => props.answers.forWhomPeople ?? 1)
const insureds: Ref<Insured[]> = ref([])

const validationRules = {
  required
}

let hiddenOptions: Array<
  OptionsValuesInterface & { maxSelectable: number; positionIndex: number }
> = []
const validRelationships = props.configurations!.relationships.filter(
  (r) => !r.holder && !r.default && !r.disabled
)
const availableOptions: Ref<OptionsValuesInterface[][]> = ref([[]])

const noRelation = computed(
  () => props.configurations!.relationships.find((r) => r.default && !r.disabled)!
)

const options = computed(() => {
  if (props.answers.hasRelationship === 'no') {
    // add default option (Otro)
    return [...availableOptions.value].map((ao) => [
      ...ao,
      { label: noRelation.value.text, value: noRelation.value.id }
    ])
  }
  return availableOptions.value
})

function getDefaultInsuredInfo() {
  const length = props.answers.forWhomPeople

  return arrayRange(1, length).map((i) => {
    const isFirst = i === 1
    const isHolder = isFirst && props.answers.forWhom !== ForWhomEnum.NOT_ME
    const role = isHolder ? UserRoleEnum.HOLDER_INSURED : UserRoleEnum.INSURED
    const holderRelationship = props.configurations?.relationships.find((r) => r.holder)!.id
    const relationship = isHolder
      ? holderRelationship
      : insureds.value[i - 1]?.relationship !== holderRelationship // check previous relationship is not holder
        ? insureds.value[i - 1]?.relationship
        : ''
    return new Insured({
      role,
      userDetails: new UserDetails({ birthDate: insureds.value[i - 1]?.userDetails.birthDate }),
      addressDetails: new AddressDetails({
        postalCode: insureds.value[i - 1]?.addressDetails.postalCode
      }),
      isHolder,
      relationship
    })
  })
}

function getInsuredValues() {
  if (props.externalInit) {
    return props.answers.insureds
  }
  return props.answers.insureds.length &&
    props.answers.insureds.length === props.answers.forWhomPeople
    ? props.answers.insureds
    : getDefaultInsuredInfo()
}

function checkForUniques(value: string, insuredIndex: number) {
  const item = props.configurations?.relationships.find((r) => r.id === value)!
  if (item.maxSelectable) {
    const index = availableOptions.value[insuredIndex].findIndex((ao) => ao.value === item.id)
    const isInList = hiddenOptions.findIndex((ho) => ho.value === value)
    if (isInList < 0 && index > 0) {
      const countUsedOption = insureds.value.filter(
        (insured) => insured.relationship === availableOptions.value[insuredIndex][index].value
      ).length
      if (countUsedOption >= item.maxSelectable) {
        const positionIndex = validRelationships.findIndex((o) => o.id === value)
        hiddenOptions.push({
          positionIndex,
          maxSelectable: item.maxSelectable,
          ...availableOptions.value[insuredIndex][index]
        })
        availableOptions.value.forEach((ao, i) => {
          if (i !== insuredIndex && insureds.value[i].relationship !== value) {
            const removeIndex = ao.findIndex((ao) => ao.value === item.id)
            ao.splice(removeIndex, 1)
          }
        })
      }
    }
  }
}

function restoreOptionValues(value: string) {
  const item = validRelationships.find((r) => r.id === value)!
  if (item) {
    const elementsToRemove: Array<
      OptionsValuesInterface & { maxSelectable: number; positionIndex: number }
    > = []
    hiddenOptions.forEach((hiddenOption) => {
      const countUsedOption = insureds.value.filter(
        (insured) => insured.relationship === hiddenOption.value
      ).length
      if (countUsedOption < hiddenOption.maxSelectable) {
        availableOptions.value.forEach((options) => {
          const isOptionIncluded = options.find((elm) => elm.value === hiddenOption.value) || null
          if (!isOptionIncluded) {
            elementsToRemove.push(hiddenOption)
            options.splice(hiddenOption.positionIndex, 0, {
              label: hiddenOption.label,
              value: hiddenOption.value
            })
          }
        })
      }
    })
    if (elementsToRemove.length > 0) {
      hiddenOptions = hiddenOptions.filter((elemento) => !elementsToRemove.includes(elemento))
    }
  }
}

function onChange(value: string | undefined, insuredIndex: number) {
  if (value) {
    checkForUniques(value, insuredIndex)
    restoreOptionValues(value)
  }
  emit('update:modelValue', insureds.value)
}

const emit = defineEmits(['update:modelValue'])

function resetOthersRelationship() {
  if (props.answers.hasRelationship === 'yes') {
    insureds.value.forEach((i) => {
      if (i.relationship === noRelation.value.id) {
        i.relationship = ''
      }
    })
  }
}

function generateOptions(insureds: Insured[]): OptionsValuesInterface[][] {
  return Array.from(
    {
      length: props.answers.forWhomPeople
    },
    (_, index) => {
      return validRelationships
        .map((r, i) => {
          if (r.maxSelectable) {
            const countUsedOption = insureds.filter(
              (insured) => insured.relationship === r.id
            ).length
            if (
              countUsedOption >= r.maxSelectable &&
              (!insureds[index] || insureds[index].relationship !== r.id)
            ) {
              const isInList = hiddenOptions.findIndex((ho) => ho.value === r.id)
              if (isInList === -1) {
                hiddenOptions.push({
                  positionIndex: i,
                  maxSelectable: r.maxSelectable,
                  label: r.text,
                  value: r.id
                })
              }
              return null
            }
          }

          return {
            label: r.text,
            value: r.id
          }
        })!
        .filter((v): v is OptionsValuesInterface => !!v)
    }
  )
}

onBeforeMount(() => {
  insureds.value = getInsuredValues()
  availableOptions.value = generateOptions(insureds.value)
  resetOthersRelationship()
})

watch(
  () => props.answers.insureds,
  () => {
    insureds.value = getInsuredValues()
    availableOptions.value = generateOptions(insureds.value)
    insureds.value.forEach((insured, i) => {
      if (insured.relationship) {
        checkForUniques(insured.relationship!, i)
        restoreOptionValues(insured.relationship)
      }
    })
  }
)

watch(
  () => props.answers.hasRelationship,
  () => {
    resetOthersRelationship()
  }
)
</script>